import { useEffect, useState, type FormEvent } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useClientFlags } from '@/hooks/useClientFlags';
import { clientMetadataService } from '@/services/clientMetadataService';
import type { DeployBlock, InactiveTag } from '@/types';
import { shortPerson } from '@/utils/clientFlags';
import { formatNzDate } from '@/utils/relativeTime';
import './clientFlags.css';

export type ClientTagKind = 'deploy-block' | 'inactive';

interface ClientTagDialogProps {
  /** Which of the two tags the dialog sets or lifts. */
  kind: ClientTagKind;
  /** The client the tag is for. Null closes the dialog. */
  clientName: string | null;
  onClose: () => void;
  /** Called once the tag is set or lifted. */
  onChanged?: (clientName: string) => void;
}

type Standing = DeployBlock | InactiveTag | undefined;

/**
 * The one dialog that sets or lifts a client tag. It opens on the tag's
 * present state: a client without the tag is asked for the reason to set it,
 * a client with it is shown the standing tag and asked to lift it. Nothing is
 * written until the person presses the button.
 */
export function ClientTagDialog({ kind, clientName, onClose, onChanged }: ClientTagDialogProps) {
  const flags = useClientFlags();
  // The client stays on screen while the dialog fades out.
  const [shown, setShown] = useState<string | null>(clientName);
  // Decided once the tags are read, then held: the tag changing under an open dialog must not swap its question.
  const [step, setStep] = useState<{ lifting: boolean; standing: Standing } | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadySet, setAlreadySet] = useState<DeployBlock | null>(null);

  useEffect(() => {
    if (!clientName) return;
    setShown(clientName);
    setStep(null);
    setReason('');
    setError(null);
    setAlreadySet(null);
  }, [clientName]);

  const tagsLoading = flags.status === 'loading';
  useEffect(() => {
    if (!clientName || step || tagsLoading) return;
    const standing = kind === 'deploy-block' ? flags.deployBlock(clientName) : flags.inactive(clientName);
    setStep({ lifting: Boolean(standing), standing });
  }, [clientName, step, tagsLoading, flags, kind]);

  const name = shown ?? '';
  const lifting = step?.lifting ?? false;
  const reasonRequired = kind === 'deploy-block' && !lifting;
  const canConfirm = Boolean(step) && !busy && !alreadySet && (!reasonRequired || reason.trim().length > 0);

  const confirm = async (event?: FormEvent) => {
    event?.preventDefault();
    // The dialog can open from inside another form. Its own submit must not reach that one.
    event?.stopPropagation();
    if (!canConfirm || !name) return;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'deploy-block') {
        if (lifting) {
          await clientMetadataService.liftDeployBlock(name);
        } else {
          const result = await clientMetadataService.setDeployBlock(name, reason);
          if (!result.changed && result.tag) {
            // Somebody else blocked the client first. Their reason stands, and this one was not saved.
            setAlreadySet(result.tag);
            return;
          }
        }
      } else if (lifting) {
        await clientMetadataService.liftInactive(name);
      } else {
        await clientMetadataService.setInactive(name, reason);
      }
      onChanged?.(name);
      onClose();
    } catch (e) {
      console.error(`Could not change the ${kind} tag for ${name}`, e);
      const detail = e instanceof Error ? ` ${e.message}` : '';
      setError(`Nothing was changed.${detail}`);
    } finally {
      setBusy(false);
    }
  };

  let title: string;
  let effect: string;
  let confirmLabel: string;
  if (!step) {
    // Which question to ask is not known until the tags are read.
    title = kind === 'deploy-block' ? `Deploys to ${name}` : `State of ${name}`;
    effect = '';
    confirmLabel = kind === 'deploy-block' ? 'Block deploys' : 'Mark inactive';
  } else if (kind === 'deploy-block') {
    title = lifting ? `Allow deploys to ${name} again?` : `Block deploys to ${name}?`;
    effect = lifting
      ? `Deploys to ${name} then start as usual, alone or in a group.`
      : `A single deploy to ${name} then shows your reason and asks for the client's name, and a group deploy skips ${name}.`;
    confirmLabel = lifting ? 'Allow deploys again' : 'Block deploys';
  } else {
    title = lifting ? `Mark ${name} active again?` : `Mark ${name} inactive?`;
    effect = lifting
      ? `${name} goes back into the client pickers, the All Production Clients group, Home's counts and the Credits table.`
      : `${name} leaves the client pickers, the All Production Clients group, Home's counts and the Credits table, unless a person asks to see inactive clients. Nothing changes in its workspace, and a deploy to it still works.`;
    confirmLabel = lifting ? 'Mark active again' : 'Mark inactive';
  }

  const standing = step?.standing;
  const reasonId = `client-tag-reason-${kind}`;

  return (
    <Modal show={Boolean(clientName)} onHide={busy ? undefined : onClose} centered>
      <Form onSubmit={confirm}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!step ? (
            <p className="client-flag-dialog__effect">
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Reading the client's tags
            </p>
          ) : (
            <>
              {lifting && standing && (
                <dl className="client-flag-dialog__facts">
                  {(standing.reason || kind === 'deploy-block') && (
                    <>
                      <dt>Reason</dt>
                      <dd>{standing.reason || 'No reason was given'}</dd>
                    </>
                  )}
                  <dt>Set by</dt>
                  <dd title={standing.setBy}>{shortPerson(standing.setBy) || 'Unknown'}</dd>
                  <dt>Set on</dt>
                  <dd>{formatNzDate(standing.setAt) || 'Unknown'}</dd>
                </dl>
              )}
              <p className="client-flag-dialog__effect">{effect}</p>
              {!lifting && !alreadySet && (
                <Form.Group controlId={reasonId}>
                  <Form.Label>{reasonRequired ? 'Reason' : 'Reason (optional)'}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={busy}
                    autoFocus
                    placeholder={
                      kind === 'deploy-block'
                        ? 'Held on its present image until their server is patched'
                        : 'Trial ended and the customer did not go on'
                    }
                  />
                  {reasonRequired && (
                    <Form.Text>Anyone who tries to deploy to this client reads this, with your name.</Form.Text>
                  )}
                </Form.Group>
              )}
            </>
          )}
          {alreadySet && (
            <Alert variant="warning" className="mb-0">
              {shortPerson(alreadySet.setBy) || 'Somebody'} blocked deploys to {name} a moment ago, so your reason was
              not saved. Their reason: {alreadySet.reason}
            </Alert>
          )}
          {flags.status === 'error' && !error && (
            <Alert variant="warning" className="mt-3 mb-0">
              The newest read of the client tags failed, so what is shown here can be out of date. {flags.error}
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="mt-3 mb-0">
              {error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={busy}>
            {alreadySet ? 'Close' : 'Cancel'}
          </Button>
          {!alreadySet && (
            <Button type="submit" variant="primary" disabled={!canConfirm}>
              {busy && <Spinner size="sm" className="me-2" />}
              {confirmLabel}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/** Block deploys to a client, or allow them again. */
export function DeployBlockDialog(props: Omit<ClientTagDialogProps, 'kind'>) {
  return <ClientTagDialog kind="deploy-block" {...props} />;
}

/** Mark a client inactive, or active again. */
export function InactiveDialog(props: Omit<ClientTagDialogProps, 'kind'>) {
  return <ClientTagDialog kind="inactive" {...props} />;
}

export default ClientTagDialog;
